/**
 * El plan del club · `endpoints.md` §A.2.1
 *
 * Tres cosas que el panel necesita saber en cada arranque, y que salen de una sola
 * llamada (`GET /clubs/{club}/plan`):
 *
 * - **Qué funciones tiene**, para *ocultar* lo que el plan no incluye en lugar de
 *   dejar que el club choque con un `403 plan_feature_missing`.
 * - **El uso frente a los límites** —«52 de 60 alumnos»—, que avisan y no cortan.
 * - **Si está al día con Kiap.** `due` es el aviso de ponerse al día; `lapsed`, que
 *   el panel ya está en solo lectura.
 *
 * El plan **no es un rol**: que el club tenga una función no quiere decir que esta
 * persona la pueda usar. Las dos cosas se miran por separado, y los permisos de rol
 * siguen en `permisos.ts`.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';

export type FuncionDelPlan =
  'campeonatos' | 'roles' | 'multisede' | 'benchmarking';

export type SituacionDelPago = 'current' | 'due' | 'lapsed' | 'none';

export interface PlanDelClub {
  readonly plan: {
    readonly code: string;
    readonly name: string;
    readonly price_cents: number | null;
    readonly currency: string;
    readonly billing_period: string;
  } | null;
  readonly features: readonly string[] | null;
  /** Función → plan público más barato que la incluye. Ver `comoConseguirla`. */
  readonly upgrades: Readonly<Record<string, string>>;
  readonly usage: {
    readonly students: { readonly used: number; readonly max: number | null };
    readonly venues: { readonly used: number; readonly max: number | null };
  };
  readonly over_limit: boolean;
  readonly payment: {
    readonly standing: SituacionDelPago;
    /** Eden las entrega como `Date` (ver `formato.ts`): se pintan con `fecha()`. */
    readonly paid_through: Date | string | null;
    readonly grace_days: number | null;
    readonly grace_ends_on: Date | string | null;
    readonly days_left: number | null;
  };
}

/**
 * Si el club tiene esta función.
 *
 * **Mientras no se sabe, sí.** Esconder «Competición» durante el medio segundo
 * que tarda la consulta haría parpadear el menú en cada carga, y si la consulta
 * falla es mejor dejar que el servidor diga que no —con su `403` y su
 * explicación— que esconder algo que el club sí ha pagado. Sin suscripción viva
 * (`features: null`) tampoco se corta nada: es la regla del servidor.
 */
export function tieneFuncion(
  plan: PlanDelClub | undefined,
  funcion: FuncionDelPlan
): boolean {
  if (!plan?.features) return true;
  return plan.features.includes(funcion);
}

/**
 * Qué decir cuando el plan no incluye una función.
 *
 * **El nombre del plan no está escrito aquí**: lo manda el servidor en `upgrades`,
 * calculado desde los planes (el público más barato que la incluye). Si Kiap mueve
 * una función de plan desde el backoffice, este texto cambia solo. Sin nombre —
 * ningún plan público la tiene— no se ofrece nada que no existe.
 */
export function comoConseguirla(
  plan: PlanDelClub | undefined,
  funcion: FuncionDelPlan
): string {
  const nombre = plan?.upgrades[funcion];
  const contacto =
    'Si os interesa, escribidnos a hola@kiapclubs.com y lo cambiamos.';
  return nombre
    ? `Viene con el plan ${nombre} o superior. ${contacto}`
    : `Escribidnos a hola@kiapclubs.com si os interesa.`;
}

export const clavePlan = (club: string) => ['plan', club] as const;

export function usePlanDelClub(club: string | null) {
  return useQuery({
    queryKey: clavePlan(club ?? ''),
    enabled: Boolean(club),
    // Cambia cuando Kiap anota un cobro, no mientras se navega: con cinco minutos
    // basta, y al volver a la pestaña se refresca solo.
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club: club! }).plan.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as PlanDelClub;
    },
  });
}
